import {
  UserAuthContext,
  type UserAuthContextType,
  type UserAuthPayload,
  UserAuthService,
  UserService,
} from '@billx/api';
import type { User } from '@billx/types';
import {
  ensureError,
  getUserAuthTokens,
  removeUserAuthTokens,
} from '@billx/utils';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { useNotification } from '@/hooks';
import { getDeviceInfo } from '@/lib/deviceManager';
import { logger } from '@/lib/logger';
import { addBreadcrumb, clearSentryUser, setSentryUser } from '@/lib/sentry';

interface UserAuthProviderProps {
  children: React.ReactNode;
}

export const UserAuthProvider: React.FC<UserAuthProviderProps> = ({
  children,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [authUser, setAuthUser] = useState<User | null>(null);
  const { showNotification } = useNotification();
  const queryClient = useQueryClient();

  // Helper function to initialize auth state from JWT tokens
  const initializeAuthFromTokens = async () => {
    const { accessToken } = getUserAuthTokens();
    if (!accessToken) {
      setIsLoading(false);
      return;
    }

    try {
      // Fetch current user from API
      const response = await UserService.fetchCurrentUser();
      if (!response.success) {
        throw new Error(response.message);
      }

      // update queryclient cache with the current user data to avoid refetching
      queryClient.setQueryData(
        ['currentUser', response.data.id],
        response.data
      );
      const userData = response.data;

      if (userData?.id) {
        setAuthUser(userData);

        // Set user context in Sentry
        setSentryUser({
          id: userData.id,
          email: userData.email || undefined,
          username: userData.displayName || undefined,
        });

        // Log user authentication
        logger.logUserAction('user_authenticated', {
          userId: userData.id,
          email: userData.email,
          displayName: userData.displayName,
        });
      }
    } catch (error) {
      // Token might be invalid, clear it
      logger.warn('Failed to initialize auth from tokens:', { error });
      setAuthUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    initializeAuthFromTokens();
  }, []);

  const requestOtp = async (phone: string): Promise<string> => {
    try {
      const response = await UserAuthService.requestOtp(phone);
      if (!response.success) {
        throw new Error(response.message || 'Failed to send OTP');
      }
      return 'otp-sent';
    } catch (error) {
      console.error('Phone OTP error:', error);
      showNotification({
        message: ensureError(error, 'Failed to send verification code').message,
        severity: 'error',
        duration: 5000,
      });
      throw error;
    }
  };

  const verifyOtp = async (payload: UserAuthPayload): Promise<void> => {
    try {
      const deviceInfo = getDeviceInfo();

      const response = await UserAuthService.verifyOtp({
        ...payload,
        deviceId: deviceInfo.deviceId,
        deviceInfo,
      });

      if (!response.success) {
        throw new Error(response.message || 'Verification failed');
      }

      const { accessToken, refreshToken } = response.data ?? {};

      if (!accessToken || !refreshToken) {
        throw new Error('Failed to get auth tokens');
      }

      // Store tokens
      localStorage.setItem('user:accessToken', accessToken);
      localStorage.setItem('user:refreshToken', refreshToken);

      // Fetch full user data after login
      const currentUserResponse = await UserService.fetchCurrentUser();
      if (!currentUserResponse.success) {
        throw new Error(
          currentUserResponse.message || 'Failed to fetch user data'
        );
      }

      const fullUserData = currentUserResponse.data;

      // Update query client cache
      if (fullUserData?.id) {
        queryClient.setQueryData(
          ['currentUser', fullUserData.id],
          fullUserData
        );

        setAuthUser(fullUserData);

        setSentryUser({
          id: fullUserData.id,
          email: fullUserData.email || undefined,
          username: fullUserData.displayName || undefined,
        });

        logger.logUserAction('user_authenticated', {
          userId: fullUserData.id,
          displayName: fullUserData.displayName,
        });
      }
    } catch (error) {
      console.error('Login error:', error);
      showNotification({
        message: ensureError(error, 'Login failed').message,
        severity: 'error',
        duration: 5000,
      });
      throw error;
    }
  };

  const logout = async () => {
    setIsLoggingOut(true);
    logger.debug('Sign out called');

    addBreadcrumb({
      message: 'User initiated logout',
      category: 'auth',
      level: 'info',
    });

    try {
      const deviceInfo = getDeviceInfo();
      const tokens = getUserAuthTokens();

      if (tokens.refreshToken) {
        try {
          await UserAuthService.logout(deviceInfo.deviceId);
        } catch (e) {
          logger.warn('Failed to call logout API', { error: e });
        }
      }

      // Clear local storage
      removeUserAuthTokens();

      logger.debug('Sign out successful');
      setAuthUser(null);
      queryClient.clear();

      // Clear user context in Sentry
      clearSentryUser();

      addBreadcrumb({
        message: 'User logged out',
        category: 'auth',
        level: 'info',
      });
    } catch (error) {
      logger.error('Sign out error:', { error });
      showNotification({
        message: ensureError(error, 'Sign out failed').message,
        severity: 'error',
        duration: 5000,
      });
    } finally {
      setIsLoggingOut(false);
    }
  };

  const value: UserAuthContextType = {
    isLoading,
    authUser,
    requestOtp,
    verifyOtp,
    logout,
    isLoggingOut,
  };

  return (
    <UserAuthContext.Provider value={value}>
      {children}
    </UserAuthContext.Provider>
  );
};
